/**
 * Checkbox - the shared tickbox, with its label and its hit area.
 *
 * Atomic Design role: Atom. Renders the box, an optional label beside it, and
 * an optional description underneath. The caller owns the state.
 *
 * Thirty-five hand-rolled `<input type="checkbox">` elements were spread across
 * fifteen files before this existed, and they had drifted the way copies do:
 * `w-16px h-16px`, `h-16px w-16px`, `w-18px h-18px`, `w-20px h-20px`, four
 * different focus rings, and three different ways of pairing the box with its
 * text. None of them met the 44px touch floor — a 16px box inside a bare
 * `<label>` is a 16px target on a phone, which is the screen most of this app's
 * field use happens on.
 *
 * The fix is the same trick ToggleSwitch uses: the visible box stays small, the
 * <label> around it carries `min-h-44px` so the thing a finger can actually hit
 * is the whole row. Where a checkbox sits inside a dense table cell and 44px of
 * height would push the row around, `hitArea="inset"` grows the target with a
 * transparent pseudo-element instead, leaving the layout untouched.
 *
 * @example
 * <Checkbox checked={isBillable} onChange={setIsBillable} label="Billable" />
 *
 * @example  // dense table cell — target grows, row height doesn't
 * <Checkbox checked={selected} onChange={toggle} hitArea="inset" ariaLabel="Select WO-42" />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * How the control reaches the 44px touch floor.
 *
 *   row   — the label row itself is min-h-44px. The default, and correct
 *           wherever the checkbox sits in a stacked list or a form.
 *   inset — the visible box keeps its size and a transparent pseudo-element
 *           extends the target outwards. For table cells and chips where the
 *           extra height would move the layout. Check the neighbours first:
 *           overlapping hit areas cause mis-taps.
 */
export type CheckboxHitArea = 'row' | 'inset';

export interface CheckboxProps {
	/** Current ticked state. */
	checked: boolean;
	/** Called with the new state when the user toggles the box. */
	onChange: (checked: boolean) => void;
	/** Text beside the box. Omit for a bare box — then pass `ariaLabel`. */
	label?: React.ReactNode;
	/** Smaller helper text under the label. */
	description?: React.ReactNode;
	/** Accessible name when there is no visible label. */
	ariaLabel?: string;
	/** Disables the control and dims it. */
	disabled?: boolean;
	/** How the 44px target is reached. Defaults to 'row'. */
	hitArea?: CheckboxHitArea;
	/** Optional id on the input, for an external <label htmlFor>. */
	id?: string;
	/** Optional name on the input. */
	name?: string;
	/** Extra classes merged onto the root. */
	className?: string;
	/** Extra classes merged onto the label text. */
	labelClassName?: string;
	/** Overrides the atom's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

/* ============================================
 * STYLE TOKENS
 * ============================================ */

/**
 * The visible box. One size, one focus ring, everywhere.
 *
 * It is the browser's own checkbox, so text colour and border do nothing to it
 * (that styling needs the forms plugin, which this app doesn't load) and a
 * ticked box came out in the system's blue. accent-color is what a native box
 * takes its colour from. The ring is keyboard-only: on `focus` it also drew
 * after every tap and stayed until focus moved, which put the blue box in an
 * orange square that read as a bug.
 */
const BOX_CLASS = 'checkbox__box w-18px h-18px flex-shrink-0 rounded accent-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed';

/* ============================================
 * COMPONENT
 * ============================================ */

export const Checkbox: React.FC<CheckboxProps> = ({
	checked,
	onChange,
	label,
	description,
	ariaLabel,
	disabled = false,
	hitArea = 'row',
	id,
	name,
	className = '',
	labelClassName = '',
	dataRef = 'atom-checkbox',
}) => {
	/* 'inset' keeps the layout still and grows the target with a transparent
	   ::before; 'row' gives the whole label row a real 44px box. Desktop drops
	   the row variant back to a compact 32px once there's a mouse. */
	const hitAreaClass = hitArea === 'inset'
		? 'checkbox--inset relative before:absolute before:-inset-12px before:content-[\'\']'
		: 'checkbox--row min-h-44px desktop:min-h-32px';

	return (
		<label
			className={[
				'checkbox',
				hitAreaClass,
				checked ? 'checkbox--checked' : '',
				disabled ? 'checkbox--disabled cursor-not-allowed opacity-60' : 'cursor-pointer',
				'inline-flex items-start gap-8px',
				className,
			].filter(Boolean).join(' ')}
			data-ref={dataRef}
		>
			<input
				type="checkbox"
				id={id}
				name={name}
				checked={checked}
				disabled={disabled}
				aria-label={label ? undefined : ariaLabel}
				onChange={(e) => onChange(e.target.checked)}
				/* mt-2px optically centres the box against the first line of a
				   multi-line label instead of letting it float to the top. */
				className={`${BOX_CLASS} ${label || description ? 'mt-2px' : ''}`}
			/>

			{(label || description) && (
				<span className="checkbox__text min-w-0">
					{label && <span className={`checkbox__label block text-14px font-sans text-brand-text ${labelClassName}`}>{label}</span>}
					{description && <span className="checkbox__description block text-12px font-sans text-brand-text-muted">{description}</span>}
				</span>
			)}
		</label>
	);
};

export default Checkbox;
